import './index.css'

import clsx from 'clsx'
import * as React from 'react'
import { connect } from 'react-redux'
import { Outlet } from 'react-router-dom'

import { AUDIO_FILE, VIDEO_FILE } from '@disco/constants/FileTypes'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import { saveTrackMetadata } from '@disco/state-management/actions/PlaylistTrackActions'
import * as ServerActions from '@disco/state-management/actions/ServerActions'
import {
  addWriterSplit,
  deleteWriterSplit,
  updateEditedTrack,
  updateWriterSplit,
} from '@disco/state-management/actions/ServerActions'
import {
  clearBulkEdit,
  clearBulkSaveResults,
  clearTrackChanges,
  isTrackDirty,
} from '@disco/state-management/reducers/rightsManagement'
import { hasUnsavedChanges } from '@disco/state-management/selectors/editor'
import { getTerritories } from '@disco/state-management/selectors/groups'
import {
  getConstanceSettings,
  getCustomFields,
  getHideTrackMetadataMoreButton,
  getHideWritersTab,
  getTerritoriesEnabled,
  isNativeTagsEnabled,
} from '@disco/state-management/selectors/user'
import { getFileTypeByExt } from '@disco/utils/file-utils'

import type { CustomField } from 'types/business'
import type { Group } from 'types/group'
import type { EditableTrack, Track, TrackId } from 'types/track'
import type { UserReducer } from 'types/user'

import { LuChevronLeft } from '@react-icons/all-files/lu/LuChevronLeft'
import { LuChevronRight } from '@react-icons/all-files/lu/LuChevronRight'
import { LuX } from '@react-icons/all-files/lu/LuX'
import TrackInfo from 'components/common/track-info'
import CopyMetadataButton from 'components/common/tracks/copy-metadata-button'
import NativeTagsTab from 'components/common/tracks/native-tags-tab'
import TrackTerritories from 'components/common/tracks/track-territories'
import { Flex, IconButton, Separator } from 'components/ds'
import { DialogFooterSlotContext } from 'components/modals/dialog-footer/slot-context'
import { DiscoModal } from 'components/ui'
import { HoverProvider } from 'components/ui/hover-provider'
import { containedModalStyles } from 'components/utils/modalStyles'

import { UnsavedChangesDialog } from 'features/rights-data/components'

import MetadataTabs from '../metadata-tabs'

function mapStateToProps(state) {
  const editedTrack = state.server.editedTrack
  const trackId = editedTrack?.id as TrackId | null
  const constanceSettings = getConstanceSettings(state)
  const splitsV2Enabled = constanceSettings?.SPLITS_V2 || false
  const bulkEditAllVersionsTabEnabled =
    !!constanceSettings?.['SUB-1052-BULK-EDIT-ALL-VERSIONS-TAB-ROLLOUT']

  return {
    ...state.server,
    user: state.user,
    hideMoreButton: getHideTrackMetadataMoreButton(state),
    hideWritersTab: getHideWritersTab(state),
    hideTerritoriesTab: !getTerritoriesEnabled(state),
    customFields: getCustomFields(state),
    territories: getTerritories(state),
    nativeTagsEnabled: isNativeTagsEnabled(state),
    editedTrack,
    hasUnsavedChanges: hasUnsavedChanges(state),
    splitsV2Enabled,
    hasRightsManagementChanges:
      splitsV2Enabled && trackId ? isTrackDirty(state, trackId) : false,
    bulkEditAllVersionsTabEnabled,
  }
}

type PendingAction = 'close' | 'nextTrack' | 'prevTrack' | null

interface EditMetadataModalProps {
  track: Track
  user: UserReducer
  editedTrack: EditableTrack
  editedTrackRefreshing: boolean
  hideMoreButton?: boolean
  hideWritersTab: boolean
  hideTerritoriesTab: boolean
  onClose: () => void
  onNextTrackClick?: () => void
  onPrevTrackClick?: () => void
  hasUnsavedChanges?: boolean
  hasRightsManagementChanges?: boolean
  splitsV2Enabled?: boolean
  saving: boolean
  customFields: CustomField[][] | null | undefined
  territories: Group[]
  nativeTagsEnabled: boolean
  updateEditedTrack: typeof updateEditedTrack
  saveTrackMetadata: typeof saveTrackMetadata
  sendMixpanelEvent: typeof sendMixpanelEvent
  addWriterSplit: typeof addWriterSplit
  deleteWriterSplit: typeof deleteWriterSplit
  updateWriterSplit: typeof updateWriterSplit
  clearTrackChanges: typeof clearTrackChanges
  clearBulkEdit: typeof clearBulkEdit
  clearBulkSaveResults: typeof clearBulkSaveResults
  userTerritories: Group[]
  updateWriterIds?: (arg0: Array<number>) => void
  bulkEditAllVersionsTabEnabled?: boolean
}

interface EditMetadataModalState {
  showUnsavedChangesDialog: boolean
  pendingAction: PendingAction
  footerSlot: HTMLElement | null
}

class EditMetadataModal extends React.Component<
  EditMetadataModalProps,
  EditMetadataModalState
> {
  state: EditMetadataModalState = {
    showUnsavedChangesDialog: false,
    pendingAction: null,
    footerSlot: null,
  }

  /**
   * Captures the footer slot node. Held in state (not a ref) so the tabs
   * re-render once it exists and their DialogFooter can portal into it.
   */
  private setFooterSlot = (node: HTMLDivElement | null) => {
    if (node !== this.state.footerSlot) this.setState({ footerSlot: node })
  }

  componentDidMount() {
    document.addEventListener('keydown', this.handleArrowKeyNavigation)
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleArrowKeyNavigation)
  }

  /**
   * Switches between tracks with the left/right arrow keys.
   * Ignored while typing in a field or while the unsaved changes dialog is open.
   */
  private handleArrowKeyNavigation = (event: KeyboardEvent) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
    if (this.state.showUnsavedChangesDialog) return
    const target = event.target as HTMLElement | null
    if (target?.closest('input, textarea, select, [contenteditable="true"]')) {
      return
    }

    if (event.key === 'ArrowLeft' && this.props.onPrevTrackClick) {
      this.handlePrevTrackClick()
    } else if (event.key === 'ArrowRight' && this.props.onNextTrackClick) {
      this.handleNextTrackClick()
    }
  }

  /**
   * Checks if the user should be warned about unsaved rights management changes.
   * Only applies when SPLITS_V2 is enabled.
   */
  private shouldWarnAboutUnsavedChanges = (): boolean => {
    const { splitsV2Enabled, hasRightsManagementChanges } = this.props
    return Boolean(splitsV2Enabled && hasRightsManagementChanges)
  }

  /**
   * Saves pending track metadata from the Tags tab. Tags themselves persist
   * as soon as they're toggled, so this only commits edits made on the other
   * tabs — matching the Save button the rest of the modal offers.
   */
  private handleSaveTrackMetadata = () => {
    this.props.saveTrackMetadata({})
  }

  /**
   * Handles close request - shows warning if there are unsaved changes.
   */
  private handleCloseRequest = () => {
    if (this.shouldWarnAboutUnsavedChanges()) {
      this.setState({ showUnsavedChangesDialog: true, pendingAction: 'close' })
    } else {
      this.cleanupAndClose()
    }
  }

  /**
   * Handles next track click - shows warning if there are unsaved changes.
   */
  private handleNextTrackClick = () => {
    if (this.shouldWarnAboutUnsavedChanges()) {
      this.setState({
        showUnsavedChangesDialog: true,
        pendingAction: 'nextTrack',
      })
    } else {
      this.cleanupAndNavigateToTrack('next')
    }
  }

  /**
   * Handles previous track click - shows warning if there are unsaved changes.
   */
  private handlePrevTrackClick = () => {
    if (this.shouldWarnAboutUnsavedChanges()) {
      this.setState({
        showUnsavedChangesDialog: true,
        pendingAction: 'prevTrack',
      })
    } else {
      this.cleanupAndNavigateToTrack('prev')
    }
  }

  /**
   * Cleans up rights management state and closes the modal.
   */
  private cleanupAndClose = () => {
    this.cleanupRightsManagementState()
    this.props.onClose()
  }

  /**
   * Cleans up rights management state and navigates to next/prev track.
   */
  private cleanupAndNavigateToTrack = (direction: 'next' | 'prev') => {
    this.cleanupRightsManagementState()
    if (direction === 'next' && this.props.onNextTrackClick) {
      this.props.onNextTrackClick()
    } else if (direction === 'prev' && this.props.onPrevTrackClick) {
      this.props.onPrevTrackClick()
    }
  }

  /**
   * Cleans up rights management state for the current track.
   * This clears any unsaved changes from the Writers tab.
   * Always clears bulk edit state and error results to ensure clean state.
   */
  private cleanupRightsManagementState = () => {
    const {
      editedTrack,
      splitsV2Enabled,
      clearTrackChanges,
      clearBulkEdit,
      clearBulkSaveResults,
    } = this.props
    if (splitsV2Enabled) {
      // Clear single track state if applicable
      if (editedTrack?.id) {
        clearTrackChanges(editedTrack.id)
      }
      // Always clear bulk edit state and results to ensure clean state
      // This prevents stale data when reopening bulk edit mode
      clearBulkEdit()
      clearBulkSaveResults()
    }
  }

  /**
   * Handles user choosing to discard changes in the warning dialog.
   */
  private handleDiscardChanges = () => {
    const { pendingAction } = this.state

    this.setState({ showUnsavedChangesDialog: false, pendingAction: null })

    switch (pendingAction) {
      case 'close':
        this.cleanupAndClose()
        break
      case 'nextTrack':
        this.cleanupAndNavigateToTrack('next')
        break
      case 'prevTrack':
        this.cleanupAndNavigateToTrack('prev')
        break
    }
  }

  /**
   * Handles user choosing to continue editing in the warning dialog.
   */
  private handleContinueEditing = () => {
    this.setState({ showUnsavedChangesDialog: false, pendingAction: null })
  }
  render() {
    const fileType = getFileTypeByExt(this.props.track.ext)
    const showMediaFields = fileType === AUDIO_FILE || fileType === VIDEO_FILE
    // Mirrors what the tab footers used to pass: the single edited track,
    // or the batch of tracks when there is no single id.
    const copyMetadataTrackIds: TrackId[] = this.props.editedTrack.id
      ? [this.props.editedTrack.id]
      : this.props.editedTrack.trackIds ?? []
    const tagsTab = this.props.editedTrack.id &&
      this.props.nativeTagsEnabled &&
      this.props.track.native_tags && (
        <NativeTagsTab
          trackId={this.props.editedTrack.id}
          tracks={[this.props.track]}
          hideCopyMetadata
          onSave={this.handleSaveTrackMetadata}
          saving={this.props.saving}
          saveDisabled={!this.props.hasUnsavedChanges}
        />
      )
    const territoriesTab =
      this.props.territories.length > 0 && !this.props.hideTerritoriesTab ? (
        <TrackTerritories
          key={this.props.editedTrack.id}
          trackId={this.props.editedTrack.id}
          tracks={[this.props.track]}
          territories={this.props.territories}
        />
      ) : null
    return (
      <>
        <DiscoModal
          onRequestClose={this.handleCloseRequest}
          hasUnsavedChanges={this.props.hasUnsavedChanges}
          isOpen
          style={{
            ...containedModalStyles,
            overlay: {
              ...containedModalStyles.overlay,
              justifyContent: 'center',
            },
            content: {
              ...containedModalStyles.content,
              borderRadius: 'var(--radius-4)',
              // Remove the container's vertical padding so the sticky
              // header/footer can pin flush to the top/bottom edges and
              // own their own spacing. Horizontal padding stays.
              padding: '0 24px',
              // Center the modal on both axes. `margin: auto` in the flex
              // overlay keeps it centered while still allowing the overlay
              // to scroll if the viewport is ever shorter than the modal.
              margin: 'auto',
              // Fixed height so every tab renders at the same size and the
              // modal doesn't resize when switching tabs. Proportional on
              // normal screens, capped so it isn't oversized on tall
              // monitors, and clamped down on short viewports.
              height: 'min(90vh, 820px)',
              maxHeight: 'calc(100vh - 32px)',
              // The tab content is the scroll container now, so the modal
              // itself must not scroll.
              overflow: 'hidden',
            },
          }}
        >
          <DialogFooterSlotContext.Provider value={this.state.footerSlot}>
            <div
              className={clsx(
                'EditMetadata EditMetadata--modal modal-width--640',
                {
                  EditMetadata_Refreshing: this.props.editedTrackRefreshing,
                },
              )}
            >
              <div className="EditMetadata_Header">
                <div className="EditMetadata_Header_Content">
                  <HoverProvider>
                    <TrackInfo
                      location="edit-metadata"
                      track={this.props.track}
                      large
                    />
                  </HoverProvider>
                </div>
                <Flex
                  align="center"
                  gap="2"
                  className="EditMetadata_Header_Actions"
                >
                  {(this.props.onNextTrackClick ||
                    this.props.onPrevTrackClick) && (
                    <IconButton
                      size="2"
                      variant="ghost"
                      color="gray"
                      aria-label="Previous track"
                      disabled={!this.props.onPrevTrackClick}
                      onClick={
                        this.props.onPrevTrackClick
                          ? this.handlePrevTrackClick
                          : undefined
                      }
                    >
                      <LuChevronLeft size={20} />
                    </IconButton>
                  )}
                  {(this.props.onNextTrackClick ||
                    this.props.onPrevTrackClick) && (
                    <IconButton
                      size="2"
                      variant="ghost"
                      color="gray"
                      aria-label="Next track"
                      disabled={!this.props.onNextTrackClick}
                      onClick={
                        this.props.onNextTrackClick
                          ? this.handleNextTrackClick
                          : undefined
                      }
                    >
                      <LuChevronRight size={20} />
                    </IconButton>
                  )}
                  {copyMetadataTrackIds.length > 0 && (
                    <>
                      <Separator
                        orientation="vertical"
                        size="1"
                        className="EditMetadata_Header_Divider"
                      />
                      <CopyMetadataButton
                        variant="icon"
                        trackIds={copyMetadataTrackIds}
                      />
                    </>
                  )}
                  <IconButton
                    size="2"
                    variant="ghost"
                    color="gray"
                    aria-label="Close"
                    onClick={this.handleCloseRequest}
                  >
                    <LuX size={20} />
                  </IconButton>
                </Flex>
              </div>
              <MetadataTabs
                keyboardShortcuts
                hideCopyMetadata
                hideMoreButton={this.props.hideMoreButton}
                track={this.props.track}
                hasUnsavedChanges={this.props.hasUnsavedChanges}
                editedTrack={this.props.editedTrack}
                saving={this.props.saving}
                showSubmitButton
                showExtraMetadata={showMediaFields}
                sendMixpanelEvent={this.props.sendMixpanelEvent}
                showLyrics={showMediaFields}
                showComments
                showWriters={
                  !this.props.hideWritersTab && fileType === AUDIO_FILE
                }
                tagsTab={tagsTab}
                territoriesTab={territoriesTab}
                customFields={this.props.customFields}
                territories={this.props.territories}
                isLoading={this.props.editedTrackRefreshing}
                updateEditedTrack={
                  this.props.editedTrackRefreshing
                    ? () => {}
                    : this.props.updateEditedTrack
                }
                saveTrackMetadata={this.props.saveTrackMetadata}
                addWriterSplit={this.props.addWriterSplit}
                deleteWriterSplit={this.props.deleteWriterSplit}
                updateWriterSplit={this.props.updateWriterSplit}
                onClose={this.handleCloseRequest}
                updateWriterIds={this.props.updateWriterIds}
                showApplyToAllVersions={
                  this.props.bulkEditAllVersionsTabEnabled &&
                  !!this.props.track.nested_tracks?.length
                }
                nestedTracks={this.props.track.nested_tracks ?? []}
              />
              {/* The active tab's DialogFooter portals in here, so it sits
                below the scrolling content rather than inside it. */}
              <div
                className="EditMetadata_FooterSlot"
                ref={this.setFooterSlot}
              />
            </div>
          </DialogFooterSlotContext.Provider>
          {/* Render nested routes (e.g., rights-data modals) */}
          <Outlet />
        </DiscoModal>
        {this.state.showUnsavedChangesDialog && (
          <UnsavedChangesDialog
            onDiscard={this.handleDiscardChanges}
            onContinueEditing={this.handleContinueEditing}
          />
        )}
      </>
    )
  }
}

export default connect(mapStateToProps, {
  ...ServerActions,
  saveTrackMetadata,
  sendMixpanelEvent,
  clearTrackChanges,
  clearBulkEdit,
  clearBulkSaveResults,
})(EditMetadataModal) as any
